import { useEffect, useRef, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import Collapse from "@mui/material/Collapse";
import BlueButton from "../../components/BlueButton";
import WhiteButton from "../../components/WhiteButton";
import GenericDatePicker from "../../components/GenericDatePicker";
import {
  buscarEmpresasAutomacoes,
  buscarEstabelecimentosRelacionados,
  criarSolicitacaoAutomacao,
  listarEmpresasDoGrupo,
} from "../../services/api-automacoes.service";
import { montarAlvosDoGrupo, normalizarEmpresasDoGrupo } from "./grupoEconomico.util";

const OPCOES_FLUXO = [
  { value: "XML_PERIODO", label: "Todos os eventos XML" },
  { value: "RUBRICAS", label: "Rúbricas (tabela)" },
  { value: "FOLHA", label: "Folha de pagamento" },
];
const OPCOES_SPED = [
  { value: "contabil", label: "Contábil" },
  { value: "contribuicoes", label: "Contribuições" },
  { value: "ecf", label: "ECF" },
  { value: "fiscal", label: "Fiscal" },
];

const GRADIENTE = "linear-gradient(135deg, #0d2847 0%, #1e4a7a 100%)";

const soDigitos = (v) => (v || "").replace(/\D/g, "");
function formatCnpj(v) {
  const d = soDigitos(v);
  return d.length === 14 ? `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12, 14)}` : v;
}
// Data local -> "AAAA-MM-DD" (sem toISOString, que desloca por fuso).
function dateToIso(date) {
  if (!(date instanceof Date) || isNaN(date.getTime())) return null;
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}
const labelEmpresa = (o) => (o ? `${o.razaoSocial} — ${formatCnpj(o.cnpj)}` : "");
const isoParaBr = (iso) => (iso ? iso.split("-").reverse().join("/") : "");

export default function NovaSolicitacaoModal({ aberto, aoFechar, aoCriar }) {
  const [tipo, setTipo] = useState("");
  const [empresa, setEmpresa] = useState(null);
  const [empresaInput, setEmpresaInput] = useState("");
  const [opcoesEmpresa, setOpcoesEmpresa] = useState([]);
  const [buscandoEmpresa, setBuscandoEmpresa] = useState(false);
  const [dataIni, setDataIni] = useState(null);
  const [dataFim, setDataFim] = useState(null);
  const [tipoFluxo, setTipoFluxo] = useState("");
  const [spedTipo, setSpedTipo] = useState("");
  const [loteDias, setLoteDias] = useState(31);
  // Refinamento por estabelecimento — EXCLUSIVO do SPED Fiscal (TXM-352): a empresa escolhida
  // pode ter matriz + filiais (mesma raiz de CNPJ); se tiver mais de 1, deixamos marcar quais
  // baixar. Nenhuma marcada (ou todas) = comportamento de sempre (todos_estabelecimentos=true).
  const [estabelecimentos, setEstabelecimentos] = useState([]);
  const [estabelecimentosSelecionados, setEstabelecimentosSelecionados] = useState([]);
  const [estabelecimentosAbertos, setEstabelecimentosAbertos] = useState(false);
  // Aplicar ao grupo econômico (TXM-655): raízes de CNPJ DIFERENTES ligadas pelo cadastro
  // (id_grupo_economico) — não confundir com matriz/filiais acima. Marcado, dispara 1 solicitação
  // por empresa do grupo, no fluxo normal (nenhuma mudança de backend/RPA).
  const [grupo, setGrupo] = useState([]);
  const [erroGrupo, setErroGrupo] = useState(false);
  const [aplicarGrupo, setAplicarGrupo] = useState(false);
  const [grupoSelecionadas, setGrupoSelecionadas] = useState([]);
  const [confirmando, setConfirmando] = useState(false);
  const [progresso, setProgresso] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState(null);
  const [info, setInfo] = useState(null);
  // AbortController da submissão em voo — o modal nunca desmonta (só o `open`
  // do Dialog alterna), então fechar não cancela nada sozinho; precisamos
  // abortar explicitamente pra "Cancelar" (e X / clique fora / Esc) valerem de fato.
  const abortRef = useRef(null);

  // reset ao abrir
  useEffect(() => {
    if (!aberto) return;
    setTipo(""); setEmpresa(null); setEmpresaInput(""); setOpcoesEmpresa([]);
    setDataIni(null); setDataFim(null); setTipoFluxo(""); setSpedTipo("");
    setLoteDias(31); setErro(null); setInfo(null); setEnviando(false);
    setEstabelecimentos([]); setEstabelecimentosSelecionados([]); setEstabelecimentosAbertos(false);
    setGrupo([]); setErroGrupo(false); setAplicarGrupo(false); setGrupoSelecionadas([]);
    setConfirmando(false); setProgresso(null);
    abortRef.current = null;
  }, [aberto]);

  // busca de empresa (debounced) — só do catálogo, seleção obrigatória
  useEffect(() => {
    if (empresa || empresaInput.trim().length < 2) { setOpcoesEmpresa([]); return; }
    setBuscandoEmpresa(true);
    const id = setTimeout(() => {
      buscarEmpresasAutomacoes(empresaInput)
        .then((lista) => { setOpcoesEmpresa(lista); setErro(null); })
        .catch((err) => { setOpcoesEmpresa([]); setErro(err?.message || "Não foi possível carregar as empresas."); })
        .finally(() => setBuscandoEmpresa(false));
    }, 300);
    return () => clearTimeout(id);
  }, [empresaInput, empresa]);

  // estabelecimentos (matriz/filiais) — só faz sentido pro SPED Fiscal (TXM-352). Refaz sempre
  // que a empresa OU o tipo de SPED mudam; qualquer seleção anterior é descartada (evita marcar
  // um CNPJ de uma empresa diferente da que está selecionada agora).
  useEffect(() => {
    setEstabelecimentosSelecionados([]);
    setEstabelecimentosAbertos(false);
    if (!empresa || tipo !== "sped" || spedTipo !== "fiscal") { setEstabelecimentos([]); return; }
    let cancelado = false;
    buscarEstabelecimentosRelacionados(empresa)
      .then((lista) => { if (!cancelado) setEstabelecimentos(lista); })
      .catch(() => { if (!cancelado) setEstabelecimentos([]); });
    return () => { cancelado = true; };
  }, [empresa, tipo, spedTipo]);

  // grupo econômico da empresa escolhida (TXM-655). Vale p/ qualquer tipo — a mudança é só na tela.
  // Refaz ao trocar a empresa e descarta a seleção anterior (evita disparar p/ empresas de OUTRO grupo).
  useEffect(() => {
    setAplicarGrupo(false);
    setGrupoSelecionadas([]);
    setErroGrupo(false);
    if (!empresa?.idGrupoEconomico) { setGrupo([]); return; }
    let cancelado = false;
    listarEmpresasDoGrupo(empresa.idGrupoEconomico)
      .then((lista) => { if (!cancelado) setGrupo(normalizarEmpresasDoGrupo(lista, empresa)); })
      // sem silêncio: se falhar, a opção sumiria e pareceria que o recurso não existe
      .catch(() => { if (!cancelado) { setGrupo([]); setErroGrupo(true); } });
    return () => { cancelado = true; };
  }, [empresa]);

  const iniIso = dateToIso(dataIni);
  const fimIso = dateToIso(dataFim);
  const periodoInvertido = !!(iniIso && fimIso && iniIso > fimIso);
  const periodoValido = !!(iniIso && fimIso && !periodoInvertido);

  const lote = Math.min(31, Math.max(1, Number(loteDias) || 31));
  const diasTotais = periodoValido ? Math.floor((Date.parse(fimIso) - Date.parse(iniIso)) / 86_400_000) + 1 : 0;
  const lotesEstimados = diasTotais > 0 ? Math.ceil(diasTotais / lote) : 0;

  const condicionalOk =
    tipo === "esocial" ? !!tipoFluxo : tipo === "sped" ? !!spedTipo : false;
  // Empresas do grupo que viram solicitação (Fiscal colapsa matriz+filial da mesma raiz: a
  // pesquisa dele já traz todos os estabelecimentos — ver grupoEconomico.util).
  const empresasGrupoMarcadas = grupo.filter((e) => grupoSelecionadas.includes(e.cnpj));
  const alvosGrupo = aplicarGrupo
    ? montarAlvosDoGrupo(empresasGrupoMarcadas, tipo === "sped" ? spedTipo : "")
    : [];
  const agrupadasPorRaiz = empresasGrupoMarcadas.length - alvosGrupo.length; // só > 0 no Fiscal
  const rotuloServico =
    tipo === "sped"
      ? `SPED ${OPCOES_SPED.find((o) => o.value === spedTipo)?.label || ""}`.trim()
      : "E-Social";
  const podeEnviar =
    !!tipo && !!empresa && periodoValido && condicionalOk && !enviando &&
    (!aplicarGrupo || alvosGrupo.length > 0);

  const enviar = async () => {
    if (!podeEnviar) return;
    setEnviando(true);
    setErro(null);
    setInfo(null);
    const controller = new AbortController();
    abortRef.current = controller;

    // Refinamento por estabelecimento (SPED Fiscal, TXM-352): se marcou 1+ e NÃO marcou todos,
    // dispara 1 solicitação por CNPJ marcado (o backend só aceita 1 cnpj_estabelecimento por
    // job). Nenhum marcado, ou todos marcados = 1 solicitação só, sem refinamento — o
    // comportamento de sempre (todos_estabelecimentos=true).
    // Com o grupo marcado o refinamento por estabelecimento é ignorado (cada empresa do grupo
    // vai com todos os estabelecimentos, o padrão).
    const cnpjsEstabelecimento =
      !aplicarGrupo &&
      spedTipo === "fiscal" &&
      estabelecimentosSelecionados.length > 0 &&
      estabelecimentosSelecionados.length < estabelecimentos.length
        ? estabelecimentosSelecionados
        : [null];

    // Cada alvo = UMA solicitação: ou uma empresa do grupo, ou (como sempre) a empresa escolhida
    // com um estabelecimento opcional.
    const alvos = aplicarGrupo
      ? alvosGrupo.map((e) => ({ cnpj: e.cnpj, razaoSocial: e.razaoSocial, cnpjEstabelecimento: null }))
      : cnpjsEstabelecimento.map((cnpjEstabelecimento) => ({
          cnpj: empresa.cnpj,
          razaoSocial: empresa.razaoSocial,
          cnpjEstabelecimento,
        }));

    const montarInput = (alvo) => ({
      cnpj: alvo.cnpj,
      razaoSocial: alvo.razaoSocial,
      tipo,
      periodoInicio: iniIso,
      periodoFim: fimIso,
      tipoFluxo: tipo === "esocial" ? tipoFluxo : undefined,
      spedTipo: tipo === "sped" ? spedTipo : undefined,
      loteDias: tipo === "esocial" ? lote : undefined,
      cnpjEstabelecimento: alvo.cnpjEstabelecimento || undefined,
    });

    try {
      if (alvos.length === 1) {
        // caminho único de sempre — sem nenhuma mudança de comportamento pra quem não usa o
        // refinamento por estabelecimento.
        const resultado = await criarSolicitacaoAutomacao(montarInput(alvos[0]), { signal: controller.signal });
        if (controller.signal.aborted) return; // cancelado enquanto esperava — ignora o resultado
        if (resultado.repetido) {
          // Não é erro — só informa e deixa o operador decidir (fechar ou ajustar).
          setInfo(resultado.mensagem);
          return;
        }
        aoCriar(resultado);
        aoFechar();
        return;
      }

      // Múltiplos alvos (estabelecimentos marcados OU empresas do grupo): dispara sequencial
      // (evita sobrecarregar a central com N POSTs simultâneos), acumulando o desfecho de cada um.
      const criados = [];
      const repetidos = [];
      const falhas = [];
      for (const [i, alvo] of alvos.entries()) {
        if (controller.signal.aborted) return;
        setProgresso({ atual: i + 1, total: alvos.length });
        const rotulo = aplicarGrupo
          ? alvo.razaoSocial || formatCnpj(alvo.cnpj)
          : formatCnpj(alvo.cnpjEstabelecimento);
        try {
          const resultado = await criarSolicitacaoAutomacao(montarInput(alvo), {
            signal: controller.signal,
          });
          if (controller.signal.aborted) return;
          if (resultado.repetido) repetidos.push(rotulo);
          else criados.push(resultado);
        } catch (e) {
          if (controller.signal.aborted || e?.code === "ERR_CANCELED" || e?.name === "CanceledError") return;
          falhas.push(rotulo);
        }
      }
      criados.forEach(aoCriar);

      if (falhas.length === 0 && repetidos.length === 0) {
        aoFechar();
        return;
      }
      const partes = [];
      if (criados.length) partes.push(`${criados.length} criada(s)`);
      if (repetidos.length) partes.push(`${repetidos.length} já existente(s)`);
      if (falhas.length) partes.push(`${falhas.length} com erro`);
      const rotuloAlvos = aplicarGrupo ? "empresa(s) do grupo" : "estabelecimento(s) selecionado(s)";
      // No grupo, diz QUAIS empresas falharam: o usuário precisa saber em quais agir.
      const detalheFalhas =
        aplicarGrupo && falhas.length
          ? ` Com erro: ${falhas.slice(0, 5).join("; ")}${falhas.length > 5 ? "…" : ""}.`
          : "";
      const msg = `${alvos.length} ${rotuloAlvos}: ${partes.join(", ")}.${detalheFalhas}`;
      if (falhas.length > 0) setErro(msg);
      else setInfo(msg);
    } catch (e) {
      if (controller.signal.aborted || e?.code === "ERR_CANCELED" || e?.name === "CanceledError") {
        return; // cancelamento deliberado (botão Cancelar / X / Esc) — não é erro
      }
      setErro(e?.message || "Não foi possível enviar a solicitação. Tente novamente.");
    } finally {
      setEnviando(false);
      setProgresso(null);
    }
  };

  // Grupo marcado com mais de 1 empresa pede confirmação ANTES de disparar — são N solicitações de
  // uma vez, e cada empresa precisa da própria procuração no e-CAC.
  const aoClicarEnviar = () => {
    if (aplicarGrupo && alvosGrupo.length > 1) { setConfirmando(true); return; }
    enviar();
  };

  // Aborta a requisição em voo (se houver) e fecha — usado por TODOS os
  // caminhos de fechar o modal (botão Cancelar, X, clique fora, Esc), senão
  // a solicitação ainda pode ser criada minutos depois, com o modal já fechado.
  const cancelar = () => {
    abortRef.current?.abort();
    aoFechar();
  };

  return (
    <Dialog open={aberto} onClose={cancelar} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "12px" } }}>
      <DialogTitle sx={{ background: GRADIENTE, color: "#fff", fontWeight: 700, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        Nova solicitação
        <IconButton onClick={cancelar} sx={{ color: "#fff" }}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          <TextField select label="Tipo *" value={tipo} onChange={(e) => setTipo(e.target.value)} size="small" fullWidth>
            <MenuItem value="esocial" disabled sx={{ opacity: 0.5 }}>E-Social (em breve)</MenuItem>
            <MenuItem value="sped">SPED</MenuItem>
          </TextField>

          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Autocomplete
                options={opcoesEmpresa}
                loading={buscandoEmpresa}
                value={empresa}
                onChange={(_, v) => { setEmpresa(v); setEmpresaInput(labelEmpresa(v)); }}
                inputValue={empresaInput}
                onInputChange={(_, v, reason) => {
                  if (reason === "input") { setEmpresaInput(v); if (empresa) setEmpresa(null); }
                  else if (reason === "clear") { setEmpresaInput(""); setEmpresa(null); }
                }}
                getOptionLabel={labelEmpresa}
                isOptionEqualToValue={(o, v) => o.cnpj === v.cnpj}
                filterOptions={(x) => x}
                noOptionsText={
                  empresaInput.trim().length < 2
                    ? "Digite ao menos 2 caracteres"
                    : "Empresa não encontrada — cadastre em Cadastro de Empresas antes de solicitar"
                }
                size="small"
                renderInput={(p) => (
                  <TextField
                    {...p}
                    label="Empresa *"
                    placeholder="Razão social ou CNPJ"
                    InputProps={{
                      ...p.InputProps,
                      endAdornment: (
                        <>
                          {buscandoEmpresa ? <CircularProgress size={16} /> : null}
                          {p.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Box>
            {/* Refinamento por estabelecimento — EXCLUSIVO do SPED Fiscal (TXM-352): a Receita
                declara EFD ICMS/IPI por estabelecimento (imposto estadual); os outros 3 SPEDs
                são consolidados pela empresa inteira, não têm esse conceito. Só aparece quando a
                empresa escolhida tem matriz+filiais cadastradas (mesma raiz de CNPJ) — a seta,
                ao lado do campo Empresa, abre/fecha a lista de checkboxes. */}
            {estabelecimentos.length > 1 && !aplicarGrupo && (
              <IconButton
                size="small"
                onClick={() => setEstabelecimentosAbertos((v) => !v)}
                aria-label="Estabelecimentos da empresa"
                aria-expanded={estabelecimentosAbertos}
                sx={{ mt: 0.5 }}
              >
                {estabelecimentosAbertos ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            )}
          </Box>

          {estabelecimentos.length > 1 && !aplicarGrupo && (
            <Box sx={{ mt: -1 }}>
              <Collapse in={estabelecimentosAbertos}>
                <FormControl size="small" fullWidth sx={{ mt: 1 }}>
                  <InputLabel id="lbl-estabelecimentos">Estabelecimentos</InputLabel>
                  <Select
                    labelId="lbl-estabelecimentos"
                    multiple
                    value={estabelecimentosSelecionados}
                    onChange={(e) => {
                      const v = e.target.value;
                      setEstabelecimentosSelecionados(typeof v === "string" ? v.split(",") : v);
                    }}
                    label="Estabelecimentos"
                    renderValue={(selecionados) =>
                      selecionados.length === 0
                        ? `Todos (${estabelecimentos.length})`
                        : `${selecionados.length} de ${estabelecimentos.length} selecionado(s)`
                    }
                  >
                    {estabelecimentos.map((est) => (
                      <MenuItem key={est.cnpj} value={est.cnpj}>
                        <Checkbox checked={estabelecimentosSelecionados.indexOf(est.cnpj) > -1} size="small" />
                        <ListItemText
                          primary={`${formatCnpj(est.cnpj)}${est.isMatriz ? " (Matriz)" : ""}`}
                          secondary={est.razaoSocial}
                        />
                      </MenuItem>
                    ))}
                  </Select>
                  <Typography sx={{ fontSize: 12, color: "#6b6e76", mt: 0.5 }}>
                    Nenhum marcado (ou todos marcados) = baixa de todos os estabelecimentos.
                  </Typography>
                </FormControl>
              </Collapse>
            </Box>
          )}

          {/* Aplicar ao grupo econômico (TXM-655): empresas de RAÍZES de CNPJ diferentes ligadas pelo
              cadastro (id_grupo_economico). Aparece quando a empresa escolhida pertence a um grupo com
              mais de 1 empresa; marcado, vira 1 solicitação por empresa. Não confundir com a seta de
              estabelecimentos acima (matriz/filiais da MESMA raiz, só SPED Fiscal). Sem mensagem de
              "Verificando…" de propósito: ela aparecia por ~meio segundo e só fazia a tela piscar. */}
          {erroGrupo && (
            <Typography sx={{ fontSize: 12, color: "#b3261e", mt: -1 }}>
              Não foi possível carregar o grupo econômico desta empresa.
            </Typography>
          )}
          {grupo.length > 1 && (
            <Box sx={{ mt: -1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={aplicarGrupo}
                    disabled={enviando}
                    onChange={(e) => {
                      const marcado = e.target.checked;
                      setAplicarGrupo(marcado);
                      setGrupoSelecionadas(marcado ? grupo.map((g) => g.cnpj) : []);
                    }}
                  />
                }
                label={`Aplicar ao grupo econômico (${grupo.length} empresas)`}
              />
              <Collapse in={aplicarGrupo}>
                <FormControl size="small" fullWidth sx={{ mt: 1 }}>
                  <InputLabel id="lbl-grupo">Empresas do grupo</InputLabel>
                  <Select
                    labelId="lbl-grupo"
                    multiple
                    value={grupoSelecionadas}
                    disabled={enviando}
                    onChange={(e) => {
                      const v = e.target.value;
                      setGrupoSelecionadas(typeof v === "string" ? v.split(",") : v);
                    }}
                    label="Empresas do grupo"
                    renderValue={(sel) => `${sel.length} de ${grupo.length} selecionada(s)`}
                  >
                    {grupo.map((g) => (
                      <MenuItem key={g.cnpj} value={g.cnpj}>
                        <Checkbox checked={grupoSelecionadas.indexOf(g.cnpj) > -1} size="small" />
                        <ListItemText
                          primary={`${formatCnpj(g.cnpj)}${g.isMatriz ? " (Matriz)" : ""}`}
                          secondary={g.razaoSocial}
                        />
                      </MenuItem>
                    ))}
                  </Select>
                  <Typography sx={{ fontSize: 12, color: "#6b6e76", mt: 0.5 }}>
                    {alvosGrupo.length === 0
                      ? "Marque ao menos uma empresa."
                      : alvosGrupo.length === 1
                        ? "Será criada 1 solicitação."
                        : `Serão criadas ${alvosGrupo.length} solicitações, uma por empresa.`}
                    {agrupadasPorRaiz > 0 &&
                      " Matriz e filiais da mesma raiz contam como uma só: o SPED Fiscal já traz todos os estabelecimentos."}
                    {alvosGrupo.length > 0 && " Cada empresa precisa de procuração ativa no e-CAC."}
                  </Typography>
                </FormControl>
              </Collapse>
            </Box>
          )}

          <Box sx={{ display: "flex", gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <GenericDatePicker label="Início do período" required value={dataIni} onChange={setDataIni} clearable onClear={() => setDataIni(null)} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <GenericDatePicker
                label="Fim do período"
                required
                value={dataFim}
                onChange={setDataFim}
                clearable
                onClear={() => setDataFim(null)}
                error={periodoInvertido}
                helperText={periodoInvertido ? "Fim antes do início" : ""}
              />
            </Box>
          </Box>

          {tipo === "esocial" && (
            <>
              <TextField select label="Tipo de coleta *" value={tipoFluxo} onChange={(e) => setTipoFluxo(e.target.value)} size="small" fullWidth>
                {OPCOES_FLUXO.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </TextField>
              <TextField
                type="number"
                label="Lote (dias)"
                value={loteDias}
                onChange={(e) => setLoteDias(e.target.value)}
                onBlur={() => setLoteDias(lote)}
                size="small"
                fullWidth
                inputProps={{ min: 1, max: 31 }}
                helperText="Tamanho da janela de cada execução. Máximo do e-Social: 31 dias."
              />
              {lotesEstimados > 0 && (
                <Typography sx={{ fontSize: 12, color: "#6b6e76" }}>
                  Período de {diasTotais} dias → <b>{lotesEstimados}</b> lote(s) de até {lote} dias cada.
                  O e-Social processa no máximo 10 coletas por dia (por CNPJ).
                </Typography>
              )}
            </>
          )}

          {tipo === "sped" && (
            <>
              <TextField select label="Tipo SPED *" value={spedTipo} onChange={(e) => setSpedTipo(e.target.value)} size="small" fullWidth>
                {OPCOES_SPED.map((o) => <MenuItem key={o.value} value={o.value} disabled={!!o.disabled} sx={o.disabled ? { opacity: 0.5 } : undefined}>{o.label}</MenuItem>)}
              </TextField>
            </>
          )}

          {erro && <Alert severity="error">{erro}</Alert>}
          {info && <Alert severity="info">{info}</Alert>}
          {enviando && progresso && progresso.total > 1 && (
            <Typography sx={{ fontSize: 12, color: "#6b6e76" }}>
              Enviando {progresso.atual} de {progresso.total}…
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <WhiteButton textValue="Cancelar" clickFunction={cancelar} />
        <BlueButton textValue="Enviar solicitação" clickFunction={aoClicarEnviar} disabled={!podeEnviar} />
      </DialogActions>

      {/* Confirmação do disparo em lote do grupo (TXM-655): são N solicitações de uma vez. */}
      <Dialog open={confirmando} onClose={() => setConfirmando(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "12px" } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Criar {alvosGrupo.length} solicitações?</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14 }}>
            Será criada <b>uma solicitação de {rotuloServico} para cada uma das {alvosGrupo.length} empresas</b> do
            grupo econômico, de {isoParaBr(iniIso)} a {isoParaBr(fimIso)}.
          </Typography>
          <Typography sx={{ fontSize: 12, color: "#6b6e76", mt: 1.5 }}>
            Cada empresa precisa ter procuração eletrônica ativa no e-CAC. As que não tiverem não terão arquivos baixados.
          </Typography>
        </DialogContent>
        <DialogActions>
          <WhiteButton textValue="Voltar" clickFunction={() => setConfirmando(false)} />
          <BlueButton textValue="Criar solicitações" clickFunction={() => { setConfirmando(false); enviar(); }} />
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
